// ─────────────────────────────────────────────
//  Modal — accessible dialog (D-11, MANDATORY a11y)
//  role="dialog" + aria-modal + aria-labelledby. On open: focus the
//  first focusable element and record the trigger. Trap Tab/Shift+Tab,
//  close on Escape, close on backdrop click, restore focus on close.
// ─────────────────────────────────────────────
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import tokens from "../theme/tokens";

const { color, space, radius, shadow, font } = tokens;

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  maxWidth = 480,
}) {
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const titleId = useRef("modal-title-" + Math.random().toString(36).slice(2, 9)).current;

  // ⚠️ The trap must be set up ONCE PER OPEN, never per render. Callers pass an
  // inline arrow for onClose, so its identity changes on every parent render —
  // with onClose in the dep array below, every keystroke in a field inside the
  // dialog tore this effect down (the cleanup restores focus to the trigger)
  // and re-ran it (the re-run focuses focusables[0], the ✕ button). A search
  // box then accepted exactly one letter at a time. Read the handler through a
  // ref so Escape still calls the CURRENT onClose without the effect depending
  // on its identity.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!isOpen) return undefined;

    // Record the trigger so focus can be restored on close.
    triggerRef.current = document.activeElement;

    const panel = panelRef.current;
    const focusables = panel ? panel.querySelectorAll(FOCUSABLE) : [];
    if (focusables.length) focusables[0].focus();
    else if (panel) panel.focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key === "Tab") {
        const items = panel ? panel.querySelectorAll(FOCUSABLE) : [];
        if (!items.length) {
          e.preventDefault();
          return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      // Restore focus to the element that opened the modal.
      const trigger = triggerRef.current;
      if (trigger && typeof trigger.focus === "function") trigger.focus();
    };
    // onClose is deliberately absent from the deps — see onCloseRef above.
  }, [isOpen]);

  if (!isOpen) return null;

  // Responsive width: never wider than 92vw, and scale with the board's
  // resolution factor (--atc-scale, set in TV mode) so it matches Prompt 5.
  // maxHeight + overflow keep tall content (relief selects) inside the screen.
  const panelMaxWidth =
    typeof maxWidth === "number"
      ? `min(calc(${maxWidth}px * var(--atc-scale, 1)), 92vw)`
      : maxWidth;

  const modal = (
    // role="presentation" because the backdrop IS decorative: closing by
    // clicking it is a mouse convenience, and the keyboard equivalent is the
    // Escape handler above (line ~40). Without the role this reads as an
    // undiscoverable control; with it, assistive tech skips it as intended.
    <div style={styles.backdrop} role="presentation" onMouseDown={onClose}>
      {/* The dialog's handler is a GUARD, not an interaction: it cancels the
          backdrop's close so a click inside the panel does not dismiss it.
          Keyboard users leave with Escape (handled above). */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{ ...styles.panel, maxWidth: panelMaxWidth }}
        // Stop backdrop close when interacting inside the panel.
        // Not an interaction, a guard: it cancels the BACKDROP's handler so a
        // click inside the dialog does not close it. Nothing is denied to a
        // keyboard user, who leaves with Escape.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div style={styles.header}>
          <h2 id={titleId} style={styles.title}>{title}</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={styles.close}
          >
            ✕
          </button>
        </div>
        <div style={styles.body}>{children}</div>
        {footer ? <div style={styles.footer}>{footer}</div> : null}
      </div>
    </div>
  );

  // Portal to <body> so the dialog escapes any CSS transform on an ancestor
  // (the TV board is transform:scaled, which would otherwise re-anchor and
  // shrink/enlarge this fixed backdrop, drifting it off-viewport).
  return typeof document !== "undefined"
    ? createPortal(modal, document.body)
    : modal;
}

const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.40)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: space.md,
    zIndex: 1100,
  },
  panel: {
    width: "100%",
    maxHeight: "88vh",
    overflowY: "auto",
    background: color.surface.card,
    borderRadius: radius.lg,
    boxShadow: shadow.lg,
    padding: space.xl,
    fontFamily: font.family,
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: space.md,
  },
  title: {
    margin: 0,
    fontSize: font.size.heading,
    fontWeight: font.weight.bold,
    color: color.text.primary,
    lineHeight: font.lineHeight.tight,
  },
  close: {
    background: "transparent",
    border: "none",
    color: color.text.secondary,
    cursor: "pointer",
    fontSize: font.size.body,
    lineHeight: 1,
    padding: space.xs,
  },
  body: {
    fontSize: font.size.body,
    color: color.text.primary,
    lineHeight: font.lineHeight.normal,
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: space.sm,
    marginTop: space.lg,
  },
};
